import { useState } from 'react';

import { Label } from '@/components/ui/label';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';

import { useSetPurchaseLimitStatus } from '../api/purchase-limits.queries';
import { describeLimit } from '../lib/purchase-limit-labels';
import type { PurchaseLimit } from '../types/purchase-limit.types';

const REASON_CLASS =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none';

/** Switches a limit off (kept for the record) or back on, with a reason. */
export function LimitStatusDialog({
  limit,
  onClose,
}: {
  limit: PurchaseLimit;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const status = useSetPurchaseLimitStatus();
  const turningOff = limit.isActive;

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={turningOff ? 'Switch this limit off?' : 'Switch this limit back on?'}
      description={
        turningOff
          ? `${describeLimit(limit)} stops applying at checkout straight away. It stays in the list and the audit log.`
          : `${describeLimit(limit)} applies at checkout again straight away.`
      }
      confirmLabel={turningOff ? 'Switch off' : 'Switch on'}
      variant={turningOff ? 'destructive' : 'default'}
      isPending={status.isPending}
      confirmDisabled={reason.trim().length < 5}
      onConfirm={() =>
        status.mutate(
          { id: limit.id, isActive: !turningOff, reason: reason.trim() },
          { onSuccess: onClose },
        )
      }
    >
      <div className="space-y-1.5 text-xs">
        <Label htmlFor="limit-status-reason" className="text-xs">
          Reason <span className="text-muted-foreground font-normal">(required)</span>
        </Label>
        <textarea
          id="limit-status-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          maxLength={500}
          className={REASON_CLASS}
        />
        {status.error instanceof ApiError && (
          <p role="alert" className="text-destructive">
            {status.error.message}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
